import { Button, Divider, FormControl, Grid, Hidden, TextField } from "@mui/material";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import { useState } from "react";
import styled from "styled-components";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import { navigate } from "@reach/router";
import { THEO_ID } from "../../views/App/AppRoot";

import { Add } from "@mui/icons-material";
import TLALabelsManager from "./TLALabelsManager";
import { v4 } from "uuid";
import { GET_DATACENTER } from "../../views/App/TLADatacenter";
import SwitchWithTitle from "../SwitchWithTitle/SwitchWithTitle";
import {
    CreateInstallContextForDatacenterMutation,
    CreateInstallContextForDatacenterMutationVariables,
} from "./__generated__/CreateInstallContextForDatacenterMutation";

export interface TLAInstallContextInput {
    name: string;
    description: string;
    labels: string[];
    nodeLabels: string[];
    command: string;
    securityToken: string;
    disableVaultSSH: boolean;
    upgradeDocker: boolean;
    privateId: string;
    privateCIDR: string;
}

const SectionTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
`;

interface Props {
    datacenterId: string;
    values: TLAInstallContextInput;
}

export const CREATE_INSTALL_CONTEXT_FOR_DATACENTER = gql`
    mutation CreateInstallContextForDatacenterMutation($input: CreateInstallContextForDatacenterInput!) {
        createInstallContextForDatacenter(input: $input) {
            id
        }
    }
`;

export default function TLAManageInstallContextsForm({ datacenterId, values }: Props): JSX.Element {
    const [name, setName] = useState<string>(values.name);
    const [description, setDescription] = useState<string>(values.description);
    const [labels, setLabels] = useState<Record<string, string>>(arrayToRecord(values.labels));
    const [nodeLabels, setNodeLabels] = useState<Record<string, string>>(arrayToRecord(values.nodeLabels));
    const [privateCIDR, setPrivateCIDR] = useState<string>(values.privateId);
    const [privateId, setPrivateId] = useState<string>(values.privateCIDR);
    const [disableVaultSSH, setDisableVaultSSH] = useState<boolean>(values.disableVaultSSH);
    const [upgradeDocker, setUpgradeDocker] = useState<boolean>(values.upgradeDocker);

    const [labelsOk, setLabelsOk] = useState<boolean>(true);
    const [nodeLabelsOk, setNodeLabelsOk] = useState<boolean>(true);

    const [createInstallContext, { loading }] = useMutation<
        CreateInstallContextForDatacenterMutation,
        CreateInstallContextForDatacenterMutationVariables
    >(CREATE_INSTALL_CONTEXT_FOR_DATACENTER);

    const regex = /([A-z0-9/+-.,])*=([A-z0-9/+-.,])*/;

    function arrayToRecord(lbls: string[]): Record<string, string> {
        const record: Record<string, string> = {};

        lbls.forEach((label) => (record[v4()] = label));
        return record;
    }

    async function onSubmitClick(): Promise<void> {
        try {
            await createInstallContext({
                variables: {
                    input: {
                        name: name.trim().length > 0 ? name : undefined,
                        description: description.trim().length > 0 ? description : undefined,
                        id: datacenterId,
                        labels: Object.values(labels),
                        nodeLabels: Object.values(nodeLabels),
                        disableVaultSSH,
                        privateCIDR: privateCIDR.trim().length > 0 ? privateCIDR : undefined,
                        privateId: privateId.trim().length > 0 ? privateId : undefined,
                        upgradeDocker,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_DATACENTER,
                        variables: {
                            id: datacenterId,
                        },
                    },
                ],
            });
            navigate(`/app/${THEO_ID}/tla/data-centers/${datacenterId}`);
            toast.success("TLA install context created", TOAST_SETTINGS);
        } catch (_e) {
            toast.error("Something went wrong", TOAST_SETTINGS);
        }
    }

    function onChangeLabel(idx: string, value: string) {
        const copyLabels = labels;
        copyLabels[idx] = value;
        setLabels(copyLabels);
        setLabelsOk(validLabels(Object.values(copyLabels)));
    }

    function onChangeNodeLabel(idx: string, value: string) {
        const copyLabels = nodeLabels;
        copyLabels[idx] = value;
        setNodeLabels(copyLabels);
        setNodeLabelsOk(validLabels(Object.values(copyLabels)));
    }

    function validLabels(lbls: string[]) {
        let isValid = true;
        lbls.forEach((label) => {
            if (!regex.test(label)) {
                isValid = false;
            }
        });
        return isValid;
    }

    function onRemoveLabel(idx: string) {
        const newList: Record<string, string> = {};
        for (let key in labels) {
            if (key !== idx) {
                newList[key] = labels[key];
            }
        }
        setLabels(newList);
        setLabelsOk(validLabels(Object.values(newList)));
    }

    function onRemoveNodeLabel(idx: string) {
        const newList: Record<string, string> = {};
        for (let key in nodeLabels) {
            if (key !== idx) {
                newList[key] = nodeLabels[key];
            }
        }
        setNodeLabels(newList);
        setNodeLabelsOk(validLabels(Object.values(newList)));
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <SectionTitle>Basic information</SectionTitle>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Name" />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={name}
                        placeholder="Name"
                        onChange={(e: any) => setName(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={6}></Grid>
            </Hidden>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Description" />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={description}
                        placeholder="Description"
                        onChange={(e: any) => setDescription(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={6}></Grid>
            </Hidden>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Private CIDR" />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={privateCIDR}
                        placeholder="Private CIDR"
                        onChange={(e: any) => setPrivateCIDR(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={6}></Grid>
            </Hidden>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Private ID" />
                    <TextField
                        variant="outlined"
                        fullWidth
                        defaultValue={privateId}
                        placeholder="Private ID"
                        onChange={(e: any) => setPrivateId(e.target.value)}
                    />
                </FormControl>
            </Grid>
            <Hidden mdDown>
                <Grid item md={6}></Grid>
            </Hidden>

            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Disable Vault SSH" />
                    <SwitchWithTitle
                        checked={disableVaultSSH}
                        onChange={() => setDisableVaultSSH(!disableVaultSSH)}
                        title="Disable Vault SSH"
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Upgrade Docker" />
                    <SwitchWithTitle
                        checked={upgradeDocker}
                        onChange={() => setUpgradeDocker(!upgradeDocker)}
                        title="Upgrade Docker"
                    />
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <Divider />
            </Grid>
            <Grid item xs={12}>
                <SectionTitle>Labels</SectionTitle>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <TLALabelsManager
                        labels={labels}
                        onChangeLabel={(idx: string, val: string) => onChangeLabel(idx, val)}
                        onRemoveLabel={(idx: string) => onRemoveLabel(idx)}
                        regex={regex}
                    />
                </FormControl>
                <FormControl fullWidth style={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        color="secondary"
                        startIcon={<Add />}
                        onClick={() => {
                            const copyLabels = labels;
                            copyLabels[v4()] = "";
                            setLabels({ ...copyLabels });
                            setLabelsOk(validLabels(Object.values(copyLabels)));
                        }}
                    >
                        Add label
                    </Button>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <SectionTitle>Node labels</SectionTitle>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                    <TLALabelsManager
                        labels={nodeLabels}
                        onChangeLabel={(idx: string, val: string) => onChangeNodeLabel(idx, val)}
                        onRemoveLabel={(idx: string) => onRemoveNodeLabel(idx)}
                        regex={regex}
                    />
                </FormControl>
                <FormControl fullWidth style={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        color="secondary"
                        startIcon={<Add />}
                        onClick={() => {
                            const copyLabels = nodeLabels;
                            copyLabels[v4()] = "";
                            setNodeLabels({ ...copyLabels });
                            setNodeLabelsOk(validLabels(Object.values(copyLabels)));
                        }}
                    >
                        Add node label
                    </Button>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                    <Button
                        onClick={onSubmitClick}
                        variant="contained"
                        size="large"
                        disabled={loading || !nodeLabelsOk || !labelsOk}
                    >
                        {loading ? `Creating...` : `Create`}
                    </Button>
                </div>
            </Grid>
        </Grid>
    );
}
